import type { GridColumnDef } from '@my/directory-api';
import { useGridLayout, useGridViews } from '@my/directory-api/hooks';
import {
  cn,
  ColumnChooser,
  ColumnResizeHandle,
  columnWidthStyle,
  GridViewMenu,
  sizedCell,
} from '@my/ui';
import type { Address } from '@my/directory-api';
import { EndpointInline, RouteStripInline } from '../../orders/components/OrderListCells';
import { nextPointTask } from '../../orders/lib/orderPoints';
import { isUnitRow } from '../../orders/lib/cargoInfo';
import { Clock, Lock } from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import {
  groupMovements,
  matchesQueueFilter,
  PLAN_QUEUE_FILTERS,
  probAccent,
  statusAccent,
  statusLabel,
  type GroupMode,
  type PlanMovement,
  type PlanQueueFilter,
} from '../lib/movements';
import { CapBadge, SectionBand } from './PlanShared';

/** Rows the queue shows at first, and adds each time it is scrolled to the end. */
const ROWS_PER_PAGE = 20;

/**
 * The queue's columns, for the Columns chooser (`useGridLayout`). The ids are
 * what saved layouts store — never rename one without mapping the old id. A
 * column added here later appears for everyone per its `defaultVisible`, after
 * the column it follows, without anyone's layout needing a reset.
 */
const PLAN_QUEUE_COLUMNS = [
  { id: 'job', label: 'Job #', locked: true },
  { id: 'customer', label: 'Customer' },
  { id: 'cargo', label: 'Cargo' },
  { id: 'info', label: 'Info' },
  // The Orders screen's route and points, one line each to keep rows dense.
  // (Replaced 'journey' — a layout still naming it simply drops it.)
  { id: 'route', label: 'Route' },
  { id: 'startPoint', label: 'Start point' },
  { id: 'nextPoint', label: 'Next point' },
  { id: 'endPoint', label: 'End point' },
  { id: 'distDur', label: 'Dist/Dur' },
  { id: 'window', label: 'Window' },
  { id: 'prob', label: 'Prob' },
  { id: 'driver', label: 'Driver' },
  { id: 'vehicle', label: 'Vehicle' },
  { id: 'trailer', label: 'Trailer' },
  // The physical unit is EQUIPMENT to a planner — it belongs with the vehicle
  // and the trailer, not with the goods.
  { id: 'unit', label: 'Tank/BIC' },
  { id: 'status', label: 'Status' },
  { id: 'co2', label: 'CO₂' },
  { id: 'cost', label: 'Cost' },
  { id: 'empty', label: 'Empty' },
  // Off until someone ticks them in Columns.
  { id: 'kind', label: 'Type', defaultVisible: false },
  { id: 'stops', label: 'Stops', defaultVisible: false },
] as const satisfies readonly GridColumnDef[];
type PlanQueueColumnId = (typeof PLAN_QUEUE_COLUMNS)[number]['id'];
const PLAN_QUEUE_COLUMN_DEFS: GridColumnDef[] = [...PLAN_QUEUE_COLUMNS];
const PLAN_QUEUE_COLUMN_LABEL = new Map<string, string>(
  PLAN_QUEUE_COLUMNS.map((c) => [c.id, c.label]),
);
/** Figures read better lined up on the right. */
const RIGHT_ALIGNED = new Set<string>(['co2', 'cost', 'empty', 'stops']);
/** Body cells' horizontal padding (px-2 both sides) — a sized cell's content is that much narrower. */
const CELL_PADDING = 16;

/** Today as yyyy-MM-dd, for the "At risk" (overdue) filter. */
function todayYmd(): string {
  return new Date().toISOString().slice(0, 10);
}

const ALLOCATION_HINT = 'Show which resource covers each stop';

/**
 * Crew cells are interactive, so they hint at it on hover — but they stay table
 * cells rather than buttons, because the row's own click (select) should still
 * happen. No text colour here: each cell sets its own, and two competing `text-*`
 * utilities in one className is order-dependent.
 */
const crewCellClass = 'px-2 py-1.5 hover:bg-slate-100/80 hover:underline';

/**
 * The row navigates to the order on double-click. A crew cell is already
 * interactive on a single click, so a quick second click there must not also
 * throw the planner onto the order screen.
 */
const stopDoubleClick = (e: React.MouseEvent) => e.stopPropagation();

/** Status-aligned dot colour per filter chip ('All' has no dot). */
const FILTER_DOT: Record<PlanQueueFilter, string | null> = {
  All: null,
  Standing: 'bg-slate-400',
  Unassigned: 'bg-amber-500',
  'At Risk': 'bg-red-500',
  'In Progress': 'bg-indigo-500',
  Planned: 'bg-violet-500',
};

/**
 * Job Queue — the unified list of live movements (un-manifested full-load
 * Orders + built Loads). Filter chips switch the view; clicking a row selects
 * it. Planning-intelligence columns (Prob / CO₂ / Cost / Empty / Dist·Dur) have
 * no backend source yet and show as placeholders.
 */
export function JobQueue({
  organisationId,
  movements,
  isLoading,
  isError,
  selectedId,
  onSelect,
  filter,
  onFilterChange,
  showCompleted,
  onShowCompletedChange,
  group,
  depotCountry,
  addressById,
  dragging,
  onDropResource,
  onOpenAllocation,
  expandedId,
  renderExpanded,
}: {
  /** Whose saved column layout applies. */
  organisationId: string | null;
  movements: PlanMovement[];
  isLoading: boolean;
  isError: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  filter: PlanQueueFilter;
  onFilterChange: (f: PlanQueueFilter) => void;
  /** Completed jobs in the queue (the switch after the chips). */
  showCompleted: boolean;
  onShowCompletedChange: (show: boolean) => void;
  /**
   * True while a resource card is being dragged. Rows only light up as drop
   * targets during a drag, so the queue doesn't hint at something that isn't
   * happening.
   */
  dragging: boolean;
  /** Drop a dragged resource onto this movement — the other way to assign. */
  onDropResource: (movementId: string) => void;
  /** Opens the per-stop allocation view, from a click on the crew columns. */
  onOpenAllocation: (movementId: string) => void;
  /** Movement whose detail row is expanded, or null. */
  expandedId: string | null;
  /**
   * Detail rendered in a full-width row directly beneath its movement. A render
   * prop so the queue stays unaware of assignments and addresses — it only knows
   * where the panel goes, not what's in it.
   */
  renderExpanded: (movement: PlanMovement) => ReactNode;
  /** Section grouping from the top bar. */
  group: GroupMode;
  /** Selected depot's country — anchors Outbound / Inbound / Local. */
  depotCountry: string | null;
  /** Locations by id — the route codes and the start / next / end sites. */
  addressById: Map<string, Address>;
}) {
  const navigate = useNavigate();
  const today = todayYmd();
  // Which columns show, and in what order: the organisation default with the
  // planner's own layout over it (the Columns button).
  const layout = useGridLayout(organisationId, 'tms', 'planJobQueue', PLAN_QUEUE_COLUMN_DEFS);
  const columnIds = layout.visibleColumnIds as PlanQueueColumnId[];
  // Saved views (the Views button): columns only. The cockpit's filters are
  // its own store's, so picking a view never changes them.
  const views = useGridViews({
    organisationId,
    app: 'tms',
    gridKey: 'planJobQueue',
    layout,
  });
  // Section, detail and message rows run the full width of whatever is shown.
  const span = columnIds.length;
  const rows = useMemo(
    () => movements.filter((m) => matchesQueueFilter(m, filter, today)),
    [movements, filter, today],
  );
  const countFor = (f: PlanQueueFilter) =>
    movements.filter((m) => matchesQueueFilter(m, f, today)).length;

  const sections = useMemo(
    () => groupMovements(rows, group, depotCountry),
    [rows, group, depotCountry],
  );

  // Rows are rendered 20 at a time, 20 more each time the list is scrolled to
  // its end. Everything is still loaded — the chip counts, the direction and
  // cargo filters and trailer availability all need the whole queue — so this
  // only spares the screen drawing hundreds of rows at once. A different chip
  // or grouping starts again from the first 20.
  const view = `${filter}|${group}`;
  const [paging, setPaging] = useState({ view, shown: ROWS_PER_PAGE });
  const shown = paging.view === view ? paging.shown : ROWS_PER_PAGE;
  const showMore = () => setPaging({ view, shown: shown + ROWS_PER_PAGE });

  // Never hide the job that is selected or open (e.g. picked from the Gantt).
  const orderedIds = useMemo(() => sections.flatMap((s) => s.rows.map((m) => m.id)), [sections]);
  const pinnedIndex = Math.max(
    selectedId ? orderedIds.indexOf(selectedId) : -1,
    expandedId ? orderedIds.indexOf(expandedId) : -1,
  );
  const limit = Math.max(shown, pinnedIndex + 1);
  const hasMore = limit < orderedIds.length;
  const visibleSections = useMemo(() => {
    let budget = limit;
    return sections
      .map((section) => {
        const visibleRows = section.rows.slice(0, Math.max(0, budget));
        budget -= visibleRows.length;
        return { ...section, visibleRows };
      })
      .filter((section) => section.visibleRows.length > 0);
  }, [sections, limit]);

  const onListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const box = e.currentTarget;
    if (hasMore && box.scrollTop + box.clientHeight >= box.scrollHeight - 120) showMore();
  };

  // A tall screen can fit more than 20 rows: keep adding until the list
  // overflows, or there is no scrollbar to scroll.
  const listRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const box = listRef.current;
    if (!box || !hasMore) return;
    if (box.scrollHeight <= box.clientHeight + 1) setPaging({ view, shown: limit + ROWS_PER_PAGE });
  }, [hasMore, limit, view]);

  return (
    <SectionBand
      label="Job Queue — Orders & Loads"
      right={<span>{movements.length} jobs today</span>}
      className="min-h-0 flex-1"
    >
      <div className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-slate-200 px-3 py-2">
        {PLAN_QUEUE_FILTERS.map((f) => {
          const active = f === filter;
          const count = countFor(f);
          const dot = FILTER_DOT[f];
          return (
            <button
              key={f}
              type="button"
              onClick={() => onFilterChange(f)}
              className={cn(
                'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition-colors',
                active
                  ? 'border-slate-700 bg-slate-700 text-white'
                  : 'border-slate-300 bg-white text-slate-500 hover:border-slate-500 hover:text-slate-700',
              )}
            >
              {dot && <span className={cn('h-1.5 w-1.5 rounded-full', dot)} aria-hidden="true" />}
              {f}
              {(f === 'All' || count > 0) && (
                <span className="text-[10px] opacity-60">({count})</span>
              )}
            </button>
          );
        })}
        <div className="mx-1 h-5 w-px bg-slate-200" aria-hidden="true" />
        <label className="inline-flex cursor-pointer items-center gap-1.5 text-[10px] font-medium text-slate-500">
          <button
            type="button"
            role="switch"
            aria-checked={showCompleted}
            onClick={() => onShowCompletedChange(!showCompleted)}
            className={cn(
              'relative inline-flex h-4 w-7 items-center rounded-full transition-colors duration-200 ease-in-out',
              showCompleted ? 'bg-blue-600' : 'bg-slate-300',
            )}
          >
            <span
              className={cn(
                'inline-block h-3 w-3 transform rounded-full bg-white shadow-sm transition-transform duration-200 ease-in-out',
                showCompleted ? 'translate-x-3.5' : 'translate-x-0.5',
              )}
            />
          </button>
          Show completed
        </label>
        <GridViewMenu
          className="ml-auto"
          views={views.views}
          selectedId={views.selected?.id ?? null}
          dirty={views.dirty}
          canShare={views.canShare}
          onSelect={views.select}
          onClear={views.clear}
          onSaveNew={views.saveAsNew}
          onUpdate={views.updateSelected}
          onRename={views.rename}
          onDelete={views.remove}
          busy={views.busy}
          error={views.error}
        />
        <ColumnChooser
          columns={layout.columns}
          onChange={layout.setColumns}
          hasOwnLayout={layout.hasOwnLayout}
          onResetMine={() => void layout.resetMine()}
          hasOrganisationLayout={layout.hasOrganisationLayout}
          canEditOrganisationDefault={layout.canEditOrganisationDefault}
          onSaveOrganisationDefault={() => void layout.saveAsOrganisationDefault()}
          onResetOrganisationDefault={() => void layout.resetOrganisationDefault()}
          onResetWidths={layout.resetWidths}
          busy={layout.busy}
          error={layout.saveError}
        />
      </div>

      <div ref={listRef} onScroll={onListScroll} className="min-h-0 flex-1 overflow-auto">
        {/* One line per cell: the queue is wider than the screen now (route +
            three points), and wrapping would squeeze any cell that can wrap.
            The Info column's lines are blocks, so they still stack. */}
        <table className="w-full border-collapse whitespace-nowrap text-xs">
          <thead className="sticky top-0 bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
            <tr>
              {columnIds.map((id) => (
                <th
                  key={id}
                  className={cn(
                    'relative overflow-hidden whitespace-nowrap px-2 py-1.5 font-semibold',
                    RIGHT_ALIGNED.has(id) && 'text-right',
                  )}
                  style={columnWidthStyle(layout.widthOf(id))}
                >
                  {PLAN_QUEUE_COLUMN_LABEL.get(id)}
                  {/* Drag to size; double-click to fit the content again. */}
                  <ColumnResizeHandle
                    onResize={(w) => layout.previewWidth(id, w)}
                    onResizeEnd={(w) =>
                      w == null ? layout.previewWidth(id, null) : layout.setWidth(id, w)
                    }
                    onReset={() => layout.setWidth(id, null)}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isError ? (
              <MessageRow span={span}>Couldn't load the job queue.</MessageRow>
            ) : isLoading ? (
              <MessageRow span={span}>Loading jobs…</MessageRow>
            ) : rows.length === 0 ? (
              <MessageRow span={span}>No jobs match this filter.</MessageRow>
            ) : (
              visibleSections.map((section) => (
                <Fragment key={section.key}>
                  {/* Flat mode returns one unlabelled section, so no header row. */}
                  {section.label && (
                    <tr>
                      <td
                        colSpan={span}
                        className="bg-slate-200 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-800"
                      >
                        {section.label}
                        <span className="ml-1.5 font-normal">{section.rows.length}</span>
                      </td>
                    </tr>
                  )}
                  {section.visibleRows.map((m) => (
                    <Fragment key={m.id}>
                      <Row
                        m={m}
                        columnIds={columnIds}
                        widthOf={layout.widthOf}
                        selected={m.id === selectedId}
                        onSelect={() => onSelect(m.id)}
                        onOpen={m.orderId ? () => navigate(`/orders/${m.orderId}`) : undefined}
                        dragging={dragging}
                        onDropResource={() => onDropResource(m.id)}
                        onOpenAllocation={() => onOpenAllocation(m.id)}
                        addressById={addressById}
                      />
                      {/* Detail sits in its own full-width row directly beneath its
                          movement, the way the Bookings board expands a booking —
                          the context stays visible above it. */}
                      {expandedId === m.id && (
                        <tr>
                          <td colSpan={span} className="bg-slate-50 px-2 py-2">
                            {renderExpanded(m)}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </Fragment>
              ))
            )}
            {!isError && !isLoading && hasMore && (
              <tr>
                <td
                  colSpan={span}
                  className="px-3 py-2 text-center text-[11px] italic text-slate-400"
                >
                  Showing {limit} of {orderedIds.length} jobs — scroll for more
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </SectionBand>
  );
}

function MessageRow({ span, children }: { span: number; children: React.ReactNode }) {
  return (
    <tr>
      <td colSpan={span} className="px-3 py-8 text-center text-slate-400">
        {children}
      </td>
    </tr>
  );
}

function Row({
  m,
  columnIds,
  widthOf,
  selected,
  onSelect,
  onOpen,
  dragging,
  onDropResource,
  onOpenAllocation,
  addressById,
}: {
  m: PlanMovement;
  /** The visible columns, in order. */
  columnIds: PlanQueueColumnId[];
  /** A sized column's width; undefined = fits its content. */
  widthOf: (id: string) => number | undefined;
  selected: boolean;
  onSelect: () => void;
  /** Double-click handler — set only for order rows (opens the order). */
  onOpen?: () => void;
  dragging: boolean;
  onDropResource: () => void;
  /** Opens the per-stop allocation view for this movement. */
  onOpenAllocation: () => void;
  addressById: Map<string, Address>;
}) {
  const [dragOver, setDragOver] = useState(false);

  // One derivation, two columns: the unit (box / barrel) sits with the
  // equipment, everything else stays with the goods.
  const unitRows = m.infoRows.filter(isUnitRow);
  const infoRows = m.infoRows.filter((row) => !isUnitRow(row));
  // A load can mix families, and then the column would be lying if it showed
  // bare codes — so the caption only appears when there is something to tell
  // apart. On the ordinary single-family job the column header says it already.
  const mixedUnits = new Set(unitRows.map((row) => row.caption)).size > 1;

  /** One cell of this row — the queue's visible columns pick which, in their order. */
  const cell = (id: PlanQueueColumnId) => {
    switch (id) {
      case 'job':
        return <td className="px-2 py-1.5 font-semibold text-slate-900">{m.jobNo}</td>;
      case 'customer':
        return <td className="px-2 py-1.5 text-slate-800">{m.customer}</td>;
      case 'cargo':
        return (
          <td className="px-2 py-1.5">
            <div className="flex gap-1">
              {m.cargo.map((c) => (
                <CapBadge key={c} label={c} />
              ))}
            </div>
          </td>
        );
      /* Info — what is ON the job: the goods, and the temperature when it is
                a temperature order. The physical unit carrying them has its own
                column beside the trailer. Same rows, from the same function, as the
                orders list's Info column, so the planner and the operator cannot be
                told different things about one job. */
      case 'info':
        return (
          <td className="px-2 py-1.5">
            {infoRows.length === 0 ? (
              <span className="text-slate-400">—</span>
            ) : (
              <div className="space-y-0.5">
                {infoRows.map((row) => (
                  <div
                    key={`${row.caption}-${row.value}`}
                    className="flex items-baseline gap-1.5 whitespace-nowrap"
                  >
                    {row.caption && (
                      <span className="text-[10px] uppercase tracking-wide text-slate-400">
                        {row.caption}
                      </span>
                    )}
                    <span
                      className={cn(
                        row.caption
                          ? 'max-w-[180px] truncate font-semibold text-slate-800'
                          : 'block max-w-[180px] truncate font-normal text-slate-600',
                      )}
                      // A product line is shortened ("Acid +2"); its title lists them all.
                      title={row.title ?? row.value}
                    >
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </td>
        );
      case 'route':
        return (
          <td className="px-2 py-1.5">
            <RouteStripInline tasks={m.tasks} addressById={addressById} pending={false} />
          </td>
        );
      case 'startPoint':
      case 'nextPoint':
      case 'endPoint': {
        const task =
          id === 'startPoint'
            ? m.tasks[0]
            : id === 'endPoint'
              ? m.tasks[m.tasks.length - 1]
              : nextPointTask(m.tasks);
        return (
          <td className="max-w-[250px] px-2 py-1.5">
            {id === 'nextPoint' && !task && m.tasks.length > 0 ? (
              <span className="text-[11px] italic text-slate-400">All tasks done</span>
            ) : (
              <EndpointInline
                task={task}
                address={task ? addressById.get(task.addressId) : undefined}
                pending={false}
              />
            )}
          </td>
        );
      }
      case 'distDur':
        return (
          <td
            className={cn(
              'whitespace-nowrap px-2 py-1.5',
              m.distKm == null ? 'text-slate-400' : 'text-slate-600',
            )}
            title={
              m.distKm == null
                ? undefined
                : 'Laden driving distance and time · truck route, ferry crossing not included'
            }
          >
            {m.distKm == null
              ? '—'
              : `${m.journeyApprox ? '~' : ''}${m.distKm.toLocaleString()}km${m.durLabel ? ` · ${m.durLabel}` : ''}`}
          </td>
        );
      case 'window':
        return (
          <td className="px-2 py-1.5">
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px]',
                m.window.kind === 'fixed'
                  ? 'bg-red-50 text-red-700'
                  : 'bg-slate-100 text-slate-600',
              )}
            >
              {m.window.kind === 'fixed' ? (
                <Lock className="h-3 w-3" />
              ) : (
                <Clock className="h-3 w-3" />
              )}
              {m.window.label}
            </span>
          </td>
        );
      case 'prob':
        return (
          <td className="px-2 py-1.5">
            {m.prob == null ? (
              <span className="text-slate-400">—</span>
            ) : (
              <span className={cn('font-medium', probAccent(m.prob))}>{m.prob}</span>
            )}
          </td>
        );
      /* Crew columns double as the way into the per-stop allocation view — the
                question "who's on which leg?" starts by looking at these cells, so
                that's where the answer should be reachable. Unlike the automatic
                open after an assignment, an explicit click opens regardless of stop
                count: the planner asked. */
      case 'driver':
        return (
          <td
            className={cn(crewCellClass, 'text-slate-700')}
            onClick={onOpenAllocation}
            onDoubleClick={stopDoubleClick}
            title={ALLOCATION_HINT}
          >
            {m.driver ?? <span className="italic text-slate-400">Unassigned</span>}
          </td>
        );
      case 'vehicle':
        return (
          <td
            className={cn(crewCellClass, 'font-mono text-slate-500')}
            onClick={onOpenAllocation}
            onDoubleClick={stopDoubleClick}
            title={ALLOCATION_HINT}
          >
            {m.vehicle ?? '–'}
          </td>
        );
      case 'trailer':
        return (
          <td
            className={cn(crewCellClass, 'font-mono text-slate-500')}
            onClick={onOpenAllocation}
            onDoubleClick={stopDoubleClick}
            title={ALLOCATION_HINT}
          >
            {m.trailer ?? '–'}
          </td>
        );
      /* Tank / BIC — the physical unit on the job, sitting with the equipment
                it is loaded onto rather than with the goods it carries. */
      case 'unit':
        return (
          <td className="px-2 py-1.5">
            {unitRows.length === 0 ? (
              <span className="text-slate-400">–</span>
            ) : (
              <div className="space-y-0.5">
                {unitRows.map((row) => (
                  <div
                    key={`${row.caption}-${row.value}`}
                    className="flex items-baseline gap-1.5 whitespace-nowrap"
                  >
                    {mixedUnits && (
                      <span className="text-[10px] uppercase tracking-wide text-slate-400">
                        {row.caption}
                      </span>
                    )}
                    <span className="font-mono font-semibold text-slate-700">{row.value}</span>
                  </div>
                ))}
              </div>
            )}
          </td>
        );
      case 'status':
        return (
          <td className="px-2 py-1.5">
            <span
              className={cn(
                'rounded px-1.5 py-0.5 text-[11px] font-medium',
                statusAccent(m.status),
              )}
            >
              {statusLabel(m.status)}
            </span>
          </td>
        );
      case 'co2':
        return (
          <td
            className={cn(
              'px-2 py-1.5 text-right',
              m.co2Kg == null ? 'text-slate-400' : 'text-slate-600',
            )}
            title={m.co2Kg == null ? undefined : 'Est. laden CO₂ · Tier 1, tank-to-wheel'}
          >
            {m.co2Kg == null
              ? '—'
              : `${m.journeyApprox ? '~' : ''}${Math.round(m.co2Kg).toLocaleString()}kg`}
          </td>
        );
      case 'cost':
        return (
          <td className="px-2 py-1.5 text-right text-slate-400">
            {m.costEur == null ? '—' : `€${m.costEur.toLocaleString()}`}
          </td>
        );
      case 'empty':
        return (
          <td className="px-2 py-1.5 text-right text-slate-400">
            {m.emptyKm == null ? '—' : `${m.emptyKm}km`}
          </td>
        );
      case 'kind':
        return (
          <td className="whitespace-nowrap px-2 py-1.5 text-slate-600">
            {m.kind === 'load' ? 'Load' : 'Order'}
          </td>
        );
      case 'stops':
        return <td className="px-2 py-1.5 text-right text-slate-600">{m.tasks.length}</td>;
    }
  };

  return (
    <tr
      onClick={onSelect}
      onDoubleClick={onOpen}
      onDragOver={(e) => {
        if (!dragging) return;
        // Without preventDefault the browser refuses the drop outright.
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        if (!dragging) return;
        e.preventDefault();
        setDragOver(false);
        onDropResource();
      }}
      title={onOpen ? 'Double-click to open the order' : undefined}
      className={cn(
        'cursor-pointer border-t border-slate-100 hover:bg-slate-50',
        selected && 'bg-purple-50 hover:bg-purple-50',
        // Emerald matches the Assign button — the same action by another route.
        dragOver && 'bg-emerald-50 ring-1 ring-inset ring-emerald-400 hover:bg-emerald-50',
      )}
    >
      {columnIds.map((id) => (
        <Fragment key={id}>{sizedCell(cell(id), widthOf(id), CELL_PADDING)}</Fragment>
      ))}
    </tr>
  );
}

